import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  PieChart,
  FileText,
  TrendingUp,
  Users,
  MessageSquare,
  LayoutTemplate,
  Clock,
  Download,
  Eye,
  MoreHorizontal,
  Sparkles,
  Copy,
  Check,
  Radio,
  CheckCircle2,
  FileBarChart,
  FolderKanban,
  Plus,
  Pencil,
  Layers,
  CopyPlus,
  Code2,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import type { Theme, ThemeColors, ReportCollection, ReportDefinition } from './types';
import ReportDesignerModal from './ReportDesignerModal';
import ReportCollectionModal from './ReportCollectionModal';
import ReportEmbedModal from './ReportEmbedModal';
import {
  analyticsHubService,
  mapAnalyticsHubCollection,
} from '../../../../../components/api/analyticsHubService';
import { mapAnalyticsHubReportDefinition } from './reportApiMapper';
import { formatRelativeDate, generateReportId, getReportAnalyticsHubViewUrl } from './utils';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-700 border-neutral-600 text-white',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900',
  },
};

const COLLECTION_ACCENTS = [
  'from-indigo-500 to-violet-600',
  'from-emerald-500 to-teal-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
  'from-cyan-500 to-blue-600',
  'from-violet-500 to-purple-600',
];

const ICON_MAP: Record<string, React.ReactNode> = {
  barChart: <BarChart3 className="w-5 h-5" />,
  pieChart: <PieChart className="w-5 h-5" />,
  fileText: <FileText className="w-5 h-5" />,
  trendUp: <TrendingUp className="w-5 h-5" />,
  users: <Users className="w-5 h-5" />,
  message: <MessageSquare className="w-5 h-5" />,
};

const ICON_COLORS: Record<string, string> = {
  barChart: 'from-indigo-500 to-violet-600',
  pieChart: 'from-emerald-500 to-teal-600',
  fileText: 'from-amber-500 to-orange-600',
  trendUp: 'from-rose-500 to-pink-600',
  users: 'from-cyan-500 to-blue-600',
  message: 'from-violet-500 to-purple-600',
};

export interface ChatAgentReportsProps {
  theme?: Theme;
}

const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  ready: { label: 'Ready', className: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  draft: { label: 'Draft', className: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  scheduled: { label: 'Scheduled', className: 'bg-indigo-500/15 text-indigo-400 border-indigo-500/30' },
};

function getReportUrl(report: ReportDefinition): string {
  const reportId = report.settings?.reportId ?? report.id;
  return `${window.location.origin}/portal/analytics-hub?reportId=${reportId}`;
}

interface ReportCardProps {
  report: ReportDefinition;
  colors: ThemeColors;
  copiedReportId: string | null;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onCloseMenu: () => void;
  onCopyUrl: (report: ReportDefinition) => void;
  onEdit: (report: ReportDefinition) => void;
  onClone: (report: ReportDefinition) => void;
  onEmbed: (report: ReportDefinition) => void;
}

function ReportCard({
  report,
  colors,
  copiedReportId,
  menuOpen,
  onToggleMenu,
  onCloseMenu,
  onCopyUrl,
  onEdit,
  onClone,
  onEmbed,
}: ReportCardProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const statusStyle = STATUS_STYLES[report.status];
  const gradient = ICON_COLORS[report.icon] ?? ICON_COLORS.barChart;
  const reportUrl = getReportUrl(report);
  const analyticsHubViewUrl = getReportAnalyticsHubViewUrl(report);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onCloseMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen, onCloseMenu]);

  return (
    <div
      className={`group ${colors.surface} border ${colors.border} rounded-lg transition-all hover:border-indigo-500/30`}
    >
      <div className="p-3.5">
        <div className="flex items-start justify-between mb-2.5">
          <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${gradient} flex items-center justify-center text-white`}>
            {ICON_MAP[report.icon]}
          </div>
          <div className="flex items-center gap-0.5">
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${statusStyle.className}`}>
              {statusStyle.label}
            </span>
            <button
              type="button"
              onClick={() => window.open(analyticsHubViewUrl, '_blank', 'noopener,noreferrer')}
              className={`p-1 rounded ${colors.hover} text-sky-400`}
              title={`View report: ${analyticsHubViewUrl}`}
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => window.open(reportUrl, '_blank', 'noopener,noreferrer')}
              className={`p-1 rounded ${colors.hover} text-emerald-400`}
              title="Open live report"
            >
              <Radio className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => onCopyUrl(report)}
              className={`p-1 rounded ${colors.hover} ${colors.text.muted}`}
              title="Copy report URL"
            >
              {copiedReportId === report.id ? (
                <Check className="w-3.5 h-3.5 text-emerald-400" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
            </button>
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={onToggleMenu}
                className={`p-1 rounded ${colors.hover} ${colors.text.muted} ${
                  menuOpen ? 'opacity-100 bg-neutral-700/50' : 'opacity-0 group-hover:opacity-100'
                }`}
                title="More actions"
              >
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
              {menuOpen && (
                <div
                  className={`absolute right-0 top-full mt-1 z-20 min-w-[140px] py-1 rounded-lg border shadow-xl ${colors.border} ${colors.primary}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onClone(report);
                      onCloseMenu();
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium text-left ${colors.hover} ${colors.text.secondary}`}
                  >
                    <CopyPlus className="w-3.5 h-3.5 text-sky-400" />
                    Clone
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onEmbed(report);
                      onCloseMenu();
                    }}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-[11px] font-medium text-left ${colors.hover} ${colors.text.secondary}`}
                  >
                    <Code2 className="w-3.5 h-3.5 text-indigo-400" />
                    Embed
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <h3 className={`text-xs font-semibold ${colors.text.primary} mb-0.5`}>{report.name}</h3>
        <p className={`text-[11px] ${colors.text.muted} leading-relaxed line-clamp-2 mb-2.5`}>{report.description}</p>

        <div className="flex items-center gap-2 text-[10px]">
          <span className={`px-1.5 py-0.5 rounded ${colors.secondary} ${colors.text.muted}`}>{report.category}</span>
          <span className={`flex items-center gap-0.5 ${colors.text.muted}`}>
            <Clock className="w-2.5 h-2.5" />
            {formatRelativeDate(report.lastGenerated)}
          </span>
        </div>
      </div>

      <div className={`px-3.5 py-2 border-t ${colors.border} ${colors.secondary} grid grid-cols-3 gap-1.5`}>
        <button
          type="button"
          onClick={() => window.open(analyticsHubViewUrl, '_blank', 'noopener,noreferrer')}
          className="flex items-center justify-center gap-1 py-1.5 rounded text-[10px] font-medium whitespace-nowrap bg-sky-600/15 hover:bg-sky-600/25 text-sky-400"
          title={analyticsHubViewUrl}
        >
          <Eye className="w-3 h-3 shrink-0" /> View
        </button>
        <button
          type="button"
          className={`flex items-center justify-center gap-1 py-1.5 rounded text-[10px] font-medium whitespace-nowrap ${colors.hover} ${colors.text.secondary}`}
        >
          <Download className="w-3 h-3 shrink-0" /> Export
        </button>
        <button
          type="button"
          onClick={() => onEdit(report)}
          className="flex items-center justify-center gap-1 py-1.5 rounded text-[10px] font-medium whitespace-nowrap bg-indigo-600/15 hover:bg-indigo-600/25 text-indigo-400"
        >
          <LayoutTemplate className="w-3 h-3 shrink-0" /> Edit
        </button>
      </div>
    </div>
  );
}

const ChatAgentReports: React.FC<ChatAgentReportsProps> = ({ theme = 'dark' }) => {
  const colors = THEME_CONFIG[theme];
  const [collections, setCollections] = useState<ReportCollection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);
  const [reportsCache, setReportsCache] = useState<Record<string, ReportDefinition[]>>({});
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState<string | null>(null);
  const [selectedCollectionId, setSelectedCollectionId] = useState<string>('');
  const [showDesigner, setShowDesigner] = useState(false);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState<ReportCollection | null>(null);
  const [editingReport, setEditingReport] = useState<ReportDefinition | null>(null);
  const [copiedReportId, setCopiedReportId] = useState<string | null>(null);
  const [openMenuReportId, setOpenMenuReportId] = useState<string | null>(null);
  const [embedReport, setEmbedReport] = useState<ReportDefinition | null>(null);

  const loadCollections = useCallback(async (selectAfterSave?: { isCreate: boolean; name: string; id?: string }) => {
    setCollectionsLoading(true);
    setCollectionsError(null);

    try {
      const response = await analyticsHubService.getAnalyticsHubCollections();
      const mapped = (response.data ?? []).map(mapAnalyticsHubCollection);
      setCollections(mapped);

      if (selectAfterSave) {
        if (selectAfterSave.isCreate) {
          const created =
            mapped.find((c) => c.name === selectAfterSave.name) ?? mapped[mapped.length - 1];
          if (created) setSelectedCollectionId(created.id);
        } else if (selectAfterSave.id) {
          setSelectedCollectionId(selectAfterSave.id);
        }
      } else {
        setSelectedCollectionId((current) => {
          if (current && mapped.some((c) => c.id === current)) return current;
          return mapped[0]?.id ?? '';
        });
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load collections. Please try again.';
      setCollectionsError(message);
    } finally {
      setCollectionsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCollections();
  }, [loadCollections]);

  const loadReports = useCallback(async (collectionId: string) => {
    if (!collectionId) return;

    setReportsLoading(true);
    setReportsError(null);

    try {
      const response = await analyticsHubService.getReportDefinitions(collectionId);
      const mapped = (response.data ?? []).map((item) =>
        mapAnalyticsHubReportDefinition(item, collectionId)
      );
      setReportsCache((prev) => ({ ...prev, [collectionId]: mapped }));
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load reports. Please try again.';
      setReportsError(message);
      setReportsCache((prev) => ({ ...prev, [collectionId]: [] }));
    } finally {
      setReportsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCollectionId) {
      void loadReports(selectedCollectionId);
    }
  }, [selectedCollectionId, loadReports]);

  const reportsByCollection = useMemo(() => {
    const map = new Map<string, ReportDefinition[]>();
    for (const collection of collections) {
      map.set(collection.id, reportsCache[collection.id] ?? []);
    }
    return map;
  }, [collections, reportsCache]);

  const selectedCollection =
    collections.find((c) => c.id === selectedCollectionId) ?? collections[0] ?? null;
  const selectedReports = selectedCollection
    ? reportsByCollection.get(selectedCollection.id) ?? []
    : [];
  const selectedIndex = selectedCollection
    ? collections.findIndex((c) => c.id === selectedCollection.id)
    : 0;
  const selectedAccent = COLLECTION_ACCENTS[Math.max(selectedIndex, 0) % COLLECTION_ACCENTS.length];

  const readyCount = selectedReports.filter((r) => r.status === 'ready').length;
  const draftCount = selectedReports.filter((r) => r.status === 'draft').length;

  const openDesigner = (report?: ReportDefinition) => {
    if (!selectedCollection) return;
    setEditingReport(report ?? null);
    setShowDesigner(true);
  };

  const handleCopyUrl = async (report: ReportDefinition) => {
    const url = getReportUrl(report);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedReportId(report.id);
      setTimeout(() => setCopiedReportId(null), 1500);
    } catch {
      // ignore clipboard failures
    }
  };

  const handleCloneReport = (report: ReportDefinition) => {
    if (!selectedCollection) return;
    const clone: ReportDefinition = {
      ...report,
      id: Date.now().toString(),
      name: `${report.name} (Copy)`,
      status: 'draft',
      lastGenerated: undefined,
      entities: report.entities?.map((e) => ({ ...e, id: `${e.id}-copy-${Date.now()}` })),
      settings: report.settings
        ? { ...report.settings, reportId: generateReportId() }
        : undefined,
    };
    setReportsCache((prev) => ({
      ...prev,
      [selectedCollection.id]: [clone, ...(prev[selectedCollection.id] ?? [])],
    }));
  };

  const handleEmbedReport = (report: ReportDefinition) => {
    setEmbedReport(report);
  };

  const handleSaveReport = (_partial: Partial<ReportDefinition>) => {
    if (!selectedCollection) return;
    void loadReports(selectedCollection.id);
  };

  const totalReportCount = useMemo(
    () => Object.values(reportsCache).reduce((sum, list) => sum + list.length, 0),
    [reportsCache]
  );

  const openCreateCollection = () => {
    setEditingCollection(null);
    setShowCollectionModal(true);
  };

  const openEditCollection = (collection: ReportCollection) => {
    setEditingCollection(collection);
    setShowCollectionModal(true);
  };

  const handleCollectionSaved = async (result: { isCreate: boolean; name: string; id?: string }) => {
    setShowCollectionModal(false);
    setEditingCollection(null);
    await loadCollections(result);
  };

  return (
    <div className={`h-full flex flex-col ${colors.background}`}>
      <div className={`flex-shrink-0 px-5 py-3 border-b ${colors.border} ${colors.primary}`}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className={`text-sm font-semibold ${colors.text.primary} flex items-center gap-1.5`}>
              <FileBarChart className="w-4 h-4 text-indigo-400" />
              Analytics Hub
            </h1>
            <p className={`text-[11px] ${colors.text.muted} mt-0.5`}>
              Organize report collections and design custom analytics dashboards.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openDesigner()}
            disabled={!selectedCollection}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-[11px] font-medium transition-colors"
          >
            <LayoutTemplate className="w-3.5 h-3.5" />
            Create
          </button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Collections sidebar */}
        <aside className={`w-60 flex-shrink-0 border-r ${colors.border} ${colors.primary} flex flex-col`}>
          <div className={`px-3 py-3 border-b ${colors.border}`}>
            <div className="flex items-center justify-between mb-1">
              <h2 className={`text-[10px] font-semibold uppercase tracking-wider ${colors.text.muted}`}>
                Collections
              </h2>
              <span className={`text-[10px] ${colors.text.muted}`}>{collections.length}</span>
            </div>
            <button
              type="button"
              onClick={openCreateCollection}
              className="w-full mt-2 flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-lg border border-dashed border-violet-500/40 text-violet-400 hover:bg-violet-500/10 text-[11px] font-medium transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              New Collection
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
            {collectionsLoading && (
              <div className={`flex items-center justify-center gap-2 py-8 ${colors.text.muted}`}>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-[11px]">Loading collections…</span>
              </div>
            )}

            {!collectionsLoading && collectionsError && (
              <div className="px-2 py-4 space-y-2">
                <div className={`flex items-start gap-2 text-[10px] text-red-400`}>
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                  <span>{collectionsError}</span>
                </div>
                <button
                  type="button"
                  onClick={() => void loadCollections()}
                  className="w-full px-2.5 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] font-medium"
                >
                  Retry
                </button>
              </div>
            )}

            {!collectionsLoading && !collectionsError && collections.length === 0 && (
              <div className={`px-2 py-6 text-center text-[10px] ${colors.text.muted}`}>
                No collections yet. Create one to get started.
              </div>
            )}

            {!collectionsLoading &&
              !collectionsError &&
              collections.map((collection, index) => {
              const count = reportsByCollection.get(collection.id)?.length ?? 0;
              const isActive = collection.id === selectedCollectionId;
              const accent = COLLECTION_ACCENTS[index % COLLECTION_ACCENTS.length];

              return (
                <div
                  key={collection.id}
                  className={`group flex items-center gap-1 px-1.5 py-1 rounded-lg transition-all ${
                    isActive
                      ? 'bg-indigo-600/15 border border-indigo-500/30'
                      : `border border-transparent ${colors.hover}`
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedCollectionId(collection.id)}
                    className="flex-1 flex items-center gap-2.5 py-1.5 min-w-0 text-left"
                  >
                    <div
                      className={`w-8 h-8 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center text-white flex-shrink-0 ${
                        isActive ? 'ring-2 ring-indigo-400/40' : ''
                      }`}
                    >
                      <FolderKanban className="w-3.5 h-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p
                        className={`text-[11px] font-medium truncate ${
                          isActive ? 'text-indigo-300' : colors.text.primary
                        }`}
                      >
                        {collection.name}
                      </p>
                      <p className={`text-[10px] truncate ${colors.text.muted}`}>
                        {count} report{count === 1 ? '' : 's'}
                      </p>
                    </div>
                    <span
                      className={`flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                        isActive ? 'bg-indigo-500/20 text-indigo-300' : `${colors.secondary} ${colors.text.muted}`
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditCollection(collection)}
                    className={`flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 ${colors.hover} ${colors.text.muted}`}
                    title="Edit collection"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </nav>

          <div className={`px-3 py-3 border-t ${colors.border}`}>
            <div className={`flex items-center gap-2 text-[10px] ${colors.text.muted}`}>
              <Layers className="w-3.5 h-3.5 text-violet-400" />
              <span>{totalReportCount} reports across {collections.length} collections</span>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto min-w-0">
          {selectedCollection ? (
            <div className="p-5 max-w-6xl">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${selectedAccent} flex items-center justify-center text-white flex-shrink-0 shadow-lg shadow-indigo-500/10`}>
                    <FolderKanban className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h2 className={`text-base font-semibold ${colors.text.primary}`}>
                      {selectedCollection.name}
                    </h2>
                    {selectedCollection.description && (
                      <p className={`text-[11px] ${colors.text.muted} mt-0.5 max-w-xl`}>
                        {selectedCollection.description}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => openEditCollection(selectedCollection)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md border ${colors.border} ${colors.hover} ${colors.text.secondary} text-[10px] font-medium flex-shrink-0`}
                >
                  <Pencil className="w-3 h-3" />
                  Edit
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className={`${colors.surface} border ${colors.border} rounded-lg px-3 py-2.5 flex items-center justify-between`}>
                  <div>
                    <p className={`text-[10px] ${colors.text.muted}`}>In collection</p>
                    <p className={`text-lg font-bold ${colors.text.primary}`}>{selectedReports.length}</p>
                  </div>
                  <FileBarChart className="w-4 h-4 text-indigo-400" />
                </div>
                <div className={`${colors.surface} border ${colors.border} rounded-lg px-3 py-2.5 flex items-center justify-between`}>
                  <div>
                    <p className={`text-[10px] ${colors.text.muted}`}>Ready</p>
                    <p className="text-lg font-bold text-emerald-400">{readyCount}</p>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                </div>
                <div className={`${colors.surface} border ${colors.border} rounded-lg px-3 py-2.5 flex items-center justify-between`}>
                  <div>
                    <p className={`text-[10px] ${colors.text.muted}`}>Drafts</p>
                    <p className="text-lg font-bold text-amber-400">{draftCount}</p>
                  </div>
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </div>
              </div>

              {reportsLoading && (
                <div className={`flex items-center justify-center gap-2 py-16 ${colors.text.muted}`}>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="text-[11px]">Loading reports…</span>
                </div>
              )}

              {!reportsLoading && reportsError && (
                <div className="py-12 space-y-3 text-center">
                  <div className={`flex items-center justify-center gap-2 text-[11px] text-red-400`}>
                    <AlertCircle className="w-4 h-4" />
                    <span>{reportsError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => void loadReports(selectedCollection.id)}
                    className="px-3 py-1.5 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 text-[10px] font-medium"
                  >
                    Retry
                  </button>
                </div>
              )}

              {!reportsLoading && !reportsError && selectedReports.length === 0 ? (
                <div className={`text-center py-16 rounded-xl border border-dashed ${colors.border} ${colors.surface}`}>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${selectedAccent} flex items-center justify-center mx-auto mb-4 opacity-80`}>
                    <FileBarChart className="w-7 h-7 text-white" />
                  </div>
                  <h3 className={`text-sm font-semibold ${colors.text.primary} mb-1`}>No reports yet</h3>
                  <p className={`text-[11px] ${colors.text.muted} mb-4 max-w-xs mx-auto`}>
                    Create your first report in &quot;{selectedCollection.name}&quot; using the HTML designer.
                  </p>
                  <button
                    type="button"
                    onClick={() => openDesigner()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[11px] font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Create
                  </button>
                </div>
              ) : !reportsLoading && !reportsError ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                        {selectedReports.map((report) => (
                          <ReportCard
                            key={report.id}
                            report={report}
                            colors={colors}
                            copiedReportId={copiedReportId}
                            menuOpen={openMenuReportId === report.id}
                            onToggleMenu={() =>
                              setOpenMenuReportId((prev) => (prev === report.id ? null : report.id))
                            }
                            onCloseMenu={() => setOpenMenuReportId(null)}
                            onCopyUrl={handleCopyUrl}
                            onEdit={openDesigner}
                            onClone={handleCloneReport}
                            onEmbed={handleEmbedReport}
                          />
                        ))}
                </div>
              ) : null}
            </div>
          ) : (
            <div className={`flex items-center justify-center h-full ${colors.text.muted} text-sm`}>
              Select or create a collection to get started.
            </div>
          )}
        </main>
      </div>

      {showCollectionModal && (
        <ReportCollectionModal
          theme={theme}
          collection={editingCollection}
          onClose={() => {
            setShowCollectionModal(false);
            setEditingCollection(null);
          }}
          onSaved={handleCollectionSaved}
        />
      )}

      {embedReport && (
        <ReportEmbedModal
          theme={theme}
          report={embedReport}
          onClose={() => setEmbedReport(null)}
        />
      )}

      {showDesigner && selectedCollection && (
        <ReportDesignerModal
          theme={theme}
          report={editingReport}
          collectionId={selectedCollection.id}
          onClose={() => {
            setShowDesigner(false);
            setEditingReport(null);
          }}
          onSave={handleSaveReport}
        />
      )}
    </div>
  );
};

export default ChatAgentReports;
